import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Power, Radio } from 'lucide-react';
import { askStream } from '@/lib/api-client';
import { isTriggerUtterance } from '@/lib/liveAssistTrigger';
import { isPreHearingAcked } from '@/lib/prehearing';
import { cn } from '@/lib/cn';
import type { SpeakerRoleMap, Turn } from '@/lib/types';
import { useSession } from '@/contexts/SessionContext';
import { useTranscriptionContext } from '@/contexts/TranscriptionContext';

// Live Assist (Operation Friday Ready P1-4). Watches the diarized turns for a
// question- or objection-style utterance directed at the operator, and streams
// a grounded suggested response automatically — no button press. Grounding and
// citation honesty live server-side in LIVE_ASSIST_SYSTEM (mode: 'live');
// this component only decides WHEN to generate:
//   - final segments only (interim text never triggers),
//   - debounced so a multi-part question generates once,
//   - max one generation in flight (the newest trigger wins after it lands),
//   - only while listening is on, which means the pre-hearing lawful-use
//     acknowledgement has been given; we re-check the ack explicitly anyway.

const DEBOUNCE_MS = 1_200;
// The tail of the tagged transcript sent to the server (fresher than the
// 5s-debounced PATCH copy).
const TAIL_CHARS = 2_800;

function roleLabel(speaker: number, roles: SpeakerRoleMap): string {
  return roles[String(speaker)] ?? `Speaker ${speaker}`;
}

function buildTail(turns: Turn[], roles: SpeakerRoleMap): string {
  return turns
    .map((t) => `[${roleLabel(t.speaker, roles)}] ${t.text}`)
    .join('\n')
    .slice(-TAIL_CHARS);
}

interface Suggestion {
  triggerText: string;
  answer: string;
  streaming: boolean;
  latencyMs: number | null;
  error: string | null;
}

export function LiveAssistPanel() {
  const { sessionId, rehearsal } = useSession();
  const { turns, speakerRoles, isListening } = useTranscriptionContext();

  const [enabled, setEnabled] = useState(true);
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);

  const seenTurnsRef = useRef(0);
  const debounceRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef<string | null>(null);
  const turnsRef = useRef(turns);
  const rolesRef = useRef(speakerRoles);
  turnsRef.current = turns;
  rolesRef.current = speakerRoles;

  const generate = useCallback(
    (utterance: string): void => {
      if (!sessionId || inFlightRef.current) {
        return;
      }
      // The lawful-use gate: identical condition to listening. Listening being
      // on already implies the ack, but this must hold even if that coupling
      // ever changes.
      if (!isPreHearingAcked(sessionId)) {
        return;
      }
      inFlightRef.current = true;
      const startedAt = Date.now();
      setSuggestion({ triggerText: utterance, answer: '', streaming: true, latencyMs: null, error: null });

      const finish = (): void => {
        inFlightRef.current = false;
        // A newer trigger arrived while we were generating — serve it now.
        const queued = pendingRef.current;
        pendingRef.current = null;
        if (queued !== null) {
          generate(queued);
        }
      };

      void askStream(
        {
          sessionId,
          prompt: `Respond to what was just put to the operator: "${utterance.slice(0, 1500)}"`,
          label: 'live_assist',
          mode: 'live',
          transcriptTail: buildTail(turnsRef.current, rolesRef.current),
        },
        {
          onDelta: (text) => {
            setSuggestion((prev) => (prev ? { ...prev, answer: prev.answer + text } : prev));
          },
          onDone: (envelope) => {
            setSuggestion((prev) =>
              prev
                ? {
                    ...prev,
                    answer: envelope.response.answer,
                    streaming: false,
                    latencyMs: Date.now() - startedAt,
                  }
                : prev,
            );
            finish();
          },
          onError: (err) => {
            setSuggestion((prev) =>
              prev ? { ...prev, streaming: false, error: err.message } : prev,
            );
            finish();
          },
        },
      );
    },
    [sessionId],
  );

  // Watch for new FINAL turns (interim text never lands in `turns`).
  useEffect(() => {
    if (turns.length < seenTurnsRef.current) {
      seenTurnsRef.current = turns.length; // transcript was reset
      return;
    }
    const fresh = turns.slice(seenTurnsRef.current);
    seenTurnsRef.current = turns.length;
    if (!enabled || !isListening || !sessionId) {
      return;
    }
    // Scan newest-first for a trigger not spoken by the operator themself.
    const trigger = [...fresh]
      .reverse()
      .find((t) => speakerRoles[String(t.speaker)] !== 'ME' && isTriggerUtterance(t.text));
    if (!trigger) {
      return;
    }
    if (inFlightRef.current) {
      pendingRef.current = trigger.text;
      return;
    }
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      generate(trigger.text);
    }, DEBOUNCE_MS);
  }, [turns, enabled, isListening, sessionId, speakerRoles, generate]);

  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  // Rehearsal-session feature: stay out of the way in real hearings.
  if (!rehearsal || !sessionId) {
    return null;
  }

  return (
    <div className="rounded-md border border-stone-800/70 bg-stone-900/40 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-stone-500">
          <Radio className="h-3 w-3 text-emerald-500" aria-hidden />
          Live assist
          {suggestion?.streaming ? (
            <Loader2 className="h-3 w-3 animate-spin text-stone-500" aria-hidden />
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.2em] transition-colors',
            enabled ? 'text-emerald-400 hover:text-emerald-300' : 'text-stone-600 hover:text-stone-400',
          )}
          title={enabled ? 'Live assist on — click to turn off' : 'Live assist off — click to turn on'}
        >
          <Power className="h-3 w-3" />
          {enabled ? 'On' : 'Off'}
        </button>
      </div>

      {!enabled ? null : !isListening ? (
        <p className="mt-2 text-[11px] text-stone-600">
          Waiting for listening to start. Suggested responses appear here automatically when a
          question or objection is put to you.
        </p>
      ) : suggestion ? (
        <div className="mt-2">
          <p className="truncate text-[10px] italic text-stone-500" title={suggestion.triggerText}>
            &ldquo;{suggestion.triggerText}&rdquo;
          </p>
          {suggestion.error ? (
            <p className="mt-1 text-xs text-red-400">{suggestion.error}</p>
          ) : (
            <p className="mt-1 whitespace-pre-wrap text-sm leading-snug text-stone-200">
              {suggestion.answer || '…'}
            </p>
          )}
          {suggestion.latencyMs !== null ? (
            <p className="mt-1 text-[10px] text-stone-600">
              generated in {(suggestion.latencyMs / 1000).toFixed(1)}s · grounded in the case
              materials
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-2 text-[11px] text-stone-600">
          Listening. A suggested response appears here the moment a question or objection is put to
          you.
        </p>
      )}
    </div>
  );
}
